import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';

import { useSendTvLink } from './useSendTvLink';
import styles from './OnboardingBanner.module.css';

const DISMISS_KEY = 'onboardingBannerDismissed';

/**
 * Bannière persistante sur la home MOBILE tant que le tuto n'est pas fait :
 * une seule action — recevoir le magic-link sur l'email du compte. Fermable
 * pour la session (sessionStorage), elle revient à la visite suivante.
 */
export function OnboardingBanner({ clientID, email }: { clientID: string; email: string }) {
    const { t } = useTranslation();

    // Événements onboarding_banner_email_sent / _error et lien ?from=onboarding_banner.
    const { state, send: handleSend } = useSendTvLink(email, clientID, 'onboarding_banner');
    const [dismissed, setDismissed] = useState(() => {
        try {
            return sessionStorage.getItem(DISMISS_KEY) === '1';
        } catch {
            return false; // Safari privé : la bannière revient, sans gravité.
        }
    });

    const visible = !dismissed && !!email;

    useEffect(() => {
        if (visible) logEvent('onboarding_banner_shown', clientID);
    }, []);

    if (!visible) return null;

    const handleDismiss = () => {
        try {
            sessionStorage.setItem(DISMISS_KEY, '1');
        } catch {
            /* Safari privé */
        }
        setDismissed(true);
    };

    return (
        <div className={styles.banner}>
            <span className={styles.icon}>📺</span>
            <p className={styles.text}>
                {state === 'sent' ? t('onboarding.banner.sent', { email }) : t('onboarding.banner.title')}
                {state === 'error' && <span className={styles.error}> {t('onboarding.banner.error')}</span>}
            </p>
            {state !== 'sent' && (
                <button type="button" className={styles.cta} onClick={handleSend} disabled={state === 'sending'}>
                    {state === 'sending' ? t('onboarding.banner.sending') : t('onboarding.banner.cta')}
                </button>
            )}
            <button
                type="button"
                className={styles.close}
                onClick={handleDismiss}
                aria-label={t('onboarding.banner.close')}
            >
                ✕
            </button>
        </div>
    );
}
